import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiRefreshCw, FiX } from 'react-icons/fi';
import { tasksAPI, filesAPI } from '../api';

function TranslationDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [parentTask, setParentTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);

  const loadTask = async () => {
    try {
      const data = await tasksAPI.getTaskDetails(taskId);
      setTask(data);
      
      // Also load parent task to get its directory
      if (data.parent_task_id) {
        const parentData = await tasksAPI.getTaskDetails(data.parent_task_id);
        setParentTask(parentData);
      }
    } catch (error) {
      console.error('Failed to load task:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTask();
  }, [taskId]);

  const handleDownload = async () => {
    try {
      await tasksAPI.downloadTask(taskId);
    } catch (error) {
      alert('Failed to download translation');
    }
  };

  const getLanguageName = (code) => {
    const languages = {
      'en-US': 'English',
      'es-ES': 'Spanish',
      'it-IT': 'Italian',
      'nl-NL': 'Dutch',
      'de-DE': 'German',
      'fr-FR': 'French',
    };
    return languages[code] || code;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <span className="spinner" style={{ width: '2rem', height: '2rem' }}></span>
      </div>
    );
  }

  if (!task || task.task_type !== 'translation') {
    return (
      <div className="alert alert-error">
        Translation task not found
      </div>
    );
  }

  const originalMeta = task.original_metadata || {};
  const translatedMeta = task.translated_metadata?.translated_json || task.translated_metadata || {};
  const originalLang = getLanguageName(originalMeta.origin_lang || 'fr-FR');
  const translatedLang = getLanguageName(task.target_language);

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between' }}>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          <FiArrowLeft />
          Back
        </button>
        <button className="btn btn-primary" onClick={handleDownload}>
          <FiDownload />
          Download Translation
        </button>
      </div>

      {/* Task Header */}
      <div className="detail-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <div style={{ flex: 1, marginRight: '1rem' }}>
            <h2>Translation Task #{task.id}</h2>
            <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
              {getLanguageName(originalMeta.origin_lang || 'fr-FR')} → {getLanguageName(task.target_language)}
            </p>
            <p style={{ 
              color: '#6b7280', 
              fontSize: '0.875rem',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere'
            }}>
              Original URL: {originalMeta.url}
            </p>
          </div>
          <button className="btn btn-secondary" onClick={loadTask}>
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </div>

      {/* Title Comparison */}
      <div className="comparison-container">
        <div className="comparison-column">
          <h3>ORIGINAL ({originalLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>Title</h4>
          <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>
            {originalMeta.title || 'N/A'}
          </p>
        </div>

        <div className="comparison-column">
          <h3>TRANSLATED ({translatedLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>Title</h4>
          <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>
            {translatedMeta.title || 'N/A'}
          </p>
        </div>
      </div>

      {/* Product Description Comparison */}
      <div className="comparison-container">
        <div className="comparison-column">
          <h3>ORIGINAL ({originalLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>Product Description</h4>
          <p style={{ whiteSpace: 'pre-wrap' }}>
            {originalMeta.product_description || 'N/A'}
          </p>
        </div>

        <div className="comparison-column">
          <h3>TRANSLATED ({translatedLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>Product Description</h4>
          <p style={{ whiteSpace: 'pre-wrap' }}>
            {translatedMeta.product_description || 'N/A'}
          </p>
        </div>
      </div>

      {/* About This Item Comparison */}
      <div className="comparison-container">
        <div className="comparison-column">
          <h3>ORIGINAL ({originalLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>About This Item</h4>
          {originalMeta.about_this_item && originalMeta.about_this_item.length > 0 ? (
            <ul style={{ paddingLeft: '1.5rem' }}>
              {originalMeta.about_this_item.map((item, index) => (
                <li key={index} style={{ marginBottom: '0.5rem' }}>{item}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#6b7280' }}>No information available</p>
          )}
        </div>

        <div className="comparison-column">
          <h3>TRANSLATED ({translatedLang})</h3>
          <h4 style={{ marginBottom: '0.5rem' }}>About This Item</h4>
          {translatedMeta.about_this_item && translatedMeta.about_this_item.length > 0 ? (
            <ul style={{ paddingLeft: '1.5rem' }}>
              {translatedMeta.about_this_item.map((item, index) => (
                <li key={index} style={{ marginBottom: '0.5rem' }}>{item}</li>
              ))}
            </ul>
          ) : (
            <p style={{ color: '#6b7280' }}>No information available</p>
          )}
        </div>
      </div>

      {/* Product Images Comparison */}
      {originalMeta.product_images && originalMeta.product_images.length > 0 && (
        <div className="card">
          <h3 className="card-title">Product Images Comparison</h3>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {originalMeta.product_images.map((origImage, index) => (
              <div key={index} className="comparison-container">
                <div className="comparison-column">
                  <h4>ORIGINAL ({originalLang}) - Image {index + 1}</h4>
                  <div 
                    className="image-item clickable"
                    style={{ maxWidth: '400px', margin: '1rem auto' }}
                    onClick={() => setModalImage({
                      src: filesAPI.getFileUrl(`${parentTask?.directory}/${origImage.filename}`),
                      alt: `Original Product ${index + 1}`
                    })}
                  >
                    <img 
                      src={filesAPI.getFileUrl(`${parentTask?.directory}/${origImage.filename}`)} 
                      alt={`Original Product ${index + 1}`}
                    />
                  </div>
                </div>
                <div className="comparison-column">
                  <h4>TRANSLATED ({translatedLang}) - Image {index + 1}</h4>
                  <div 
                    className="image-item clickable"
                    style={{ maxWidth: '400px', margin: '1rem auto' }}
                    onClick={() => setModalImage({
                      src: filesAPI.getFileUrl(`${task.directory}/${origImage.filename}`),
                      alt: `Translated Product ${index + 1}`
                    })}
                  >
                    <img 
                      src={filesAPI.getFileUrl(`${task.directory}/${origImage.filename}`)} 
                      alt={`Translated Product ${index + 1}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A+ Content Images Comparison */}
      {originalMeta.aplus_images && originalMeta.aplus_images.length > 0 && (
        <div className="card">
          <h3 className="card-title">A+ Content Images Comparison</h3>
          <div style={{ display: 'grid', gap: '2rem' }}>
            {originalMeta.aplus_images.map((origImage, index) => (
              <div key={index} className="comparison-container">
                <div className="comparison-column">
                  <h4>ORIGINAL ({originalLang}) - A+ Image {index + 1}</h4>
                  <div 
                    className="image-item clickable"
                    style={{ maxWidth: '400px', margin: '1rem auto' }}
                    onClick={() => setModalImage({
                      src: filesAPI.getFileUrl(`${parentTask?.directory}/${origImage.filename}`),
                      alt: `Original A+ ${index + 1}`
                    })}
                  >
                    <img 
                      src={filesAPI.getFileUrl(`${parentTask?.directory}/${origImage.filename}`)} 
                      alt={`Original A+ ${index + 1}`}
                    />
                  </div>
                </div>
                <div className="comparison-column">
                  <h4>TRANSLATED ({translatedLang}) - A+ Image {index + 1}</h4>
                  <div 
                    className="image-item clickable"
                    style={{ maxWidth: '400px', margin: '1rem auto' }}
                    onClick={() => setModalImage({
                      src: filesAPI.getFileUrl(`${task.directory}/${origImage.filename}`),
                      alt: `Translated A+ ${index + 1}`
                    })}
                  >
                    <img 
                      src={filesAPI.getFileUrl(`${task.directory}/${origImage.filename}`)} 
                      alt={`Translated A+ ${index + 1}`}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Image Modal */}
      {modalImage && (
        <div 
          className="image-modal-overlay" 
          onClick={() => setModalImage(null)}
        >
          <div className="image-modal-content">
            <button 
              className="image-modal-close"
              onClick={(e) => {
                e.stopPropagation();
                setModalImage(null);
              }}
            >
              <FiX size={24} />
            </button>
            <img 
              src={modalImage.src} 
              alt={modalImage.alt}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TranslationDetail; 